/**
 * @file ViewBenefitsPage.js
 * @description Admin-facing benefits browser with plan-year selection, year assets, and benefit plan cards.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './ViewBenefitsPage.module.scss';

import {
    searchBenefitPlans,
    searchPlanTypes,
} from '../../services/benefitPlanService';
import { fetchAssetByID } from '../../services/clientService';
import { fetchPlanYearById } from '../../services/planYearService';

import { getText } from '../../i18n';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import PlanYearDropdown from '../shared/asyncDropdowns/PlanYearDropdown';
import WelcomeBlock from '../my-benefits/_components/WelcomeBlock';
import AssetsRow from '../my-benefits/_components/AssetsRow';
import BenefitGrid from '../my-benefits/_components/BenefitGrid';
import BenefitList from '../my-benefits/_components/BenefitList';
import ViewModeToggle from '../ui/view_mode_toggle/ViewModeToggle';
import RichTextContent from '../ui/RichTextContent';
import { useAuthContext } from '../../store/Contexts';
import { BenefitsViewModeEnum, ClientOptionEnum } from '../../utils/data/enums';
import {
    readBoolOption,
    readStringOption,
    hasTextContent,
    getDefaultBenefitTextHtml,
} from '../../utils/utils';
import {
    getUserPreferences,
    updateCommunicationPreferences,
} from '../../services/individualService';
import { getClientOptions } from '../../services/clientService';
import { getEffectivePlanTypeSortOrder } from './_helpers/planTypeDisplayMode';

const ALL_BENEFIT_PLANS_COUNT = 1000;

function readBenefitsViewModeOption(options) {
    const value = readStringOption(
        options,
        ClientOptionEnum.IndividualBenefitsView,
    );
    const parsed = Number(value);
    if (
        parsed === BenefitsViewModeEnum.Grid ||
        parsed === BenefitsViewModeEnum.List
    ) {
        return parsed;
    }
    return BenefitsViewModeEnum.Grid;
}

function resolveInitialViewMode(preferences, defaultViewMode) {
    const preferenceViewMode = Number(preferences?.benefitsViewMode);
    if (
        preferenceViewMode === BenefitsViewModeEnum.Grid ||
        preferenceViewMode === BenefitsViewModeEnum.List
    ) {
        return preferenceViewMode;
    }
    return defaultViewMode;
}

function getPlanDisplayName(plan) {
    return plan?.overrideName || plan?.name || getText('msg_82a78c8f567d');
}

function normalizeSortOrder(value) {
    if (value == null || value === '') {
        return Number.MAX_SAFE_INTEGER;
    }

    const parsed = Number(value);
    return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
}

function buildPlanTypeLookup(planTypes) {
    return planTypes.reduce(function reducePlanTypes(accumulator, planType) {
        if (planType?.planTypeId == null) {
            return accumulator;
        }

        accumulator[planType.planTypeId] = planType;
        return accumulator;
    }, {});
}

function toBenefitCardRow(plan, planType) {
    const planTypeName =
        planType?.name ||
        planType?.planType?.name ||
        plan?.planType?.name ||
        '';

    return {
        id: plan.id,
        planId: plan.id,
        planTypeId: plan.planTypeId,
        planType: planTypeName,
        plan: getPlanDisplayName(plan),
        isVisible: plan.isVisible !== false,
        planTypeSortOrder: getEffectivePlanTypeSortOrder(planType),
        planTypeImageUrl: planType?.imageUrl ?? null,
    };
}

function byPlanTypeAndPlanName(a, b) {
    const sortDifference =
        normalizeSortOrder(a.planTypeSortOrder) -
        normalizeSortOrder(b.planTypeSortOrder);

    if (sortDifference !== 0) {
        return sortDifference;
    }

    const aPlanName = String(a.plan || '').toLowerCase();
    const bPlanName = String(b.plan || '').toLowerCase();
    return aPlanName.localeCompare(bPlanName);
}

function byConfiguredPlanTypeOrder(a, b) {
    const sortDifference =
        getEffectivePlanTypeSortOrder(a) - getEffectivePlanTypeSortOrder(b);

    if (sortDifference !== 0) {
        return sortDifference;
    }

    const aName = String(a?.name || a?.planType?.name || '').toLowerCase();
    const bName = String(b?.name || b?.planType?.name || '').toLowerCase();
    return aName.localeCompare(bName);
}

export default function ViewBenefitsPage() {
    const { user } = useAuthContext();
    const userId = user?.jwt?.ind ?? 0;
    const tenantUserId = user?.id;

    const [allBenefitPlanList, setAllBenefitPlanList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedPlanYearId, setSelectedPlanYearId] = useState(null);
    const [planYearDetails, setPlanYearDetails] = useState(null);
    const [planTypes, setPlanTypes] = useState([]);
    const [planYearAssetMap, setPlanYearAssetMap] = useState({});
    const [isPlanYearContextLoading, setIsPlanYearContextLoading] =
        useState(false);
    const [options, setOptions] = useState([]);
    const [userPreferences, setUserPreferences] = useState(null);
    const [viewMode, setViewMode] = useState(BenefitsViewModeEnum.Grid);

    useEffect(
        function loadDisplayPreferences() {
            if (!tenantUserId) {
                return undefined;
            }
            let cancelled = false;
            async function load() {
                try {
                    const [optionsRes, preferencesRes] = await Promise.all([
                        getClientOptions(0, [
                            ClientOptionEnum.DisplayBenefitText,
                            ClientOptionEnum.BenefitText,
                            ClientOptionEnum.DisplayEmployeeCosts,
                            ClientOptionEnum.DisplayEmployerCosts,
                            ClientOptionEnum.IndividualBenefitsView,
                        ]),
                        getUserPreferences(userId, tenantUserId).catch(
                            function onPreferencesError() {
                                return null;
                            },
                        ),
                    ]);
                    if (cancelled) {
                        return;
                    }
                    setOptions(optionsRes ?? []);
                    setUserPreferences(preferencesRes);
                    const defaultViewMode =
                        readBenefitsViewModeOption(optionsRes);
                    setViewMode(
                        resolveInitialViewMode(preferencesRes, defaultViewMode),
                    );
                } catch (err) {
                    console.error(
                        'Failed to load benefits display preferences',
                        err,
                    );
                }
            }
            load();
            return function cleanup() {
                cancelled = true;
            };
        },
        [userId, tenantUserId],
    );

    const handleViewModeChange = useCallback(
        function onViewModeChange(newViewMode) {
            setViewMode(newViewMode);
            if (!tenantUserId) {
                return;
            }
            updateCommunicationPreferences(userId, tenantUserId, {
                ...userPreferences,
                benefitsViewMode: newViewMode,
            })
                .then(function onSaved(updatedPreferences) {
                    setUserPreferences(updatedPreferences);
                })
                .catch(function onSaveError() {
                    Toast.error(getText('msg_607d98f2a881'));
                });
        },
        [userId, tenantUserId, userPreferences],
    );

    useEffect(function loadBenefitPlans() {
        let cancelled = false;

        async function load() {
            setIsLoading(true);
            try {
                const allBenefitPlansResponse = await searchBenefitPlans(
                    null,
                    1,
                    ALL_BENEFIT_PLANS_COUNT,
                );

                if (cancelled) {
                    return;
                }

                setAllBenefitPlanList(
                    allBenefitPlansResponse?.viewModels ?? [],
                );
            } catch (error) {
                console.error('Failed to fetch benefit plan list', error);
                Toast.error(
                    error?.response?.data?.detail ||
                        getText('msg_3db8e436b31b'),
                );
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        }

        load();
        return function cleanup() {
            cancelled = true;
        };
    }, []);

    useEffect(
        function loadSelectedPlanYearContext() {
            if (!selectedPlanYearId) {
                setPlanYearDetails(null);
                setPlanTypes([]);
                setPlanYearAssetMap({});
                setIsPlanYearContextLoading(false);
                return undefined;
            }

            let cancelled = false;

            async function load() {
                setIsPlanYearContextLoading(true);
                try {
                    const [selectedPlanYearDetails, selectedPlanTypes] =
                        await Promise.all([
                            fetchPlanYearById(selectedPlanYearId),
                            searchPlanTypes(null, selectedPlanYearId),
                        ]);

                    if (cancelled) {
                        return;
                    }

                    setPlanYearDetails(selectedPlanYearDetails);
                    setPlanTypes(selectedPlanTypes ?? []);

                    const assets =
                        selectedPlanYearDetails?.planYearAssets ?? [];
                    if (assets.length === 0) {
                        setPlanYearAssetMap({});
                        return;
                    }

                    const fetchedAssets = await Promise.all(
                        assets.map(function loadAsset(asset) {
                            return fetchAssetByID(asset.assetId).catch(
                                function handleFetchError() {
                                    return null;
                                },
                            );
                        }),
                    );

                    if (cancelled) {
                        return;
                    }

                    const nextAssetMap = {};
                    fetchedAssets.forEach(function indexAsset(assetDetails) {
                        if (assetDetails?.id) {
                            nextAssetMap[assetDetails.id] = assetDetails;
                        }
                    });
                    setPlanYearAssetMap(nextAssetMap);
                } catch (error) {
                    console.error(
                        'Failed to load selected plan year context',
                        error,
                    );
                    Toast.error(
                        error?.response?.data?.detail ||
                            getText('msg_ef84504ebcc1'),
                    );
                } finally {
                    if (!cancelled) {
                        setIsPlanYearContextLoading(false);
                    }
                }
            }

            load();
            return function cleanup() {
                cancelled = true;
            };
        },
        [selectedPlanYearId],
    );

    const benefitCards = useMemo(
        function buildBenefitCards() {
            if (
                !selectedPlanYearId ||
                !planTypes.length ||
                !allBenefitPlanList.length
            ) {
                return [];
            }

            const orderedPlanTypes = [...planTypes].sort(
                byConfiguredPlanTypeOrder,
            );

            const planTypeLookup = buildPlanTypeLookup(orderedPlanTypes);
            const planTypeOrderMap = orderedPlanTypes.reduce(
                function reduceOrderMap(accumulator, planType, index) {
                    if (planType?.planTypeId != null) {
                        accumulator[planType.planTypeId] = index;
                    }
                    return accumulator;
                },
                {},
            );
            const allowedPlanTypeIds = new Set(
                orderedPlanTypes
                    .map(function toPlanTypeId(planType) {
                        return planType?.planTypeId;
                    })
                    .filter(function isDefined(value) {
                        return value != null;
                    }),
            );

            return allBenefitPlanList
                .filter(function includePlan(plan) {
                    if (!plan || plan.isVisible === false) {
                        return false;
                    }

                    return allowedPlanTypeIds.has(plan.planTypeId);
                })
                .map(function mapPlan(plan) {
                    return toBenefitCardRow(
                        plan,
                        planTypeLookup[plan.planTypeId],
                    );
                })
                .sort(function byTypeOrderAndPlanName(a, b) {
                    const aTypeOrder =
                        planTypeOrderMap[a.planTypeId] ??
                        Number.MAX_SAFE_INTEGER;
                    const bTypeOrder =
                        planTypeOrderMap[b.planTypeId] ??
                        Number.MAX_SAFE_INTEGER;

                    if (aTypeOrder !== bTypeOrder) {
                        return aTypeOrder - bTypeOrder;
                    }

                    const nameCompare = String(a.plan || '').localeCompare(
                        String(b.plan || ''),
                        undefined,
                        { sensitivity: 'base' },
                    );

                    if (nameCompare !== 0) {
                        return nameCompare;
                    }

                    return byPlanTypeAndPlanName(a, b);
                });
        },
        [allBenefitPlanList, planTypes, selectedPlanYearId],
    );

    const handlePlanYearChange = useCallback(function onPlanYearChange(
        planYearId,
    ) {
        setSelectedPlanYearId(planYearId ?? null);
    }, []);

    const displayEmployeeCosts = readBoolOption(
        options,
        ClientOptionEnum.DisplayEmployeeCosts,
    );
    const displayEmployerCosts = readBoolOption(
        options,
        ClientOptionEnum.DisplayEmployerCosts,
    );
    const displayBenefitText = readBoolOption(
        options,
        ClientOptionEnum.DisplayBenefitText,
    );
    const benefitText = readStringOption(options, ClientOptionEnum.BenefitText);

    if (isLoading || isPlanYearContextLoading) {
        return <Spinner width={50} className="d-block mx-auto mt-5 pt-5" />;
    }

    return (
        <div className="page-content">
            <div className="ing-card">
                <div className="p-4 p-md-5">
                    <header className="">
                        <h1
                            className={`ing-text-heading3 mb-1 ${styles.pageTitle}`}
                        >
                            {getText('msg_927f40673764')}
                        </h1>
                        {displayBenefitText && hasTextContent(benefitText) ? (
                            <div
                                className={`ing-text-heading5 mb-0 ${styles.pageSubtitle}`}
                            >
                                <RichTextContent content={benefitText} />
                            </div>
                        ) : (
                            <div
                                className={`ing-text-heading5 mb-0 ${styles.pageSubtitle}`}
                            >
                                <RichTextContent
                                    content={getDefaultBenefitTextHtml()}
                                />
                            </div>
                        )}
                    </header>

                    <div className={`mb-4 ${styles.planYearDropdownContainer}`}>
                        <PlanYearDropdown
                            inlineLabel={false}
                            label=""
                            selectedPlanYear={selectedPlanYearId}
                            onPlanYearChange={handlePlanYearChange}
                            returnFullObject
                            autoSelectCurrent
                        />
                    </div>

                    {planYearDetails?.text && (
                        <div className="mb-4">
                            <WelcomeBlock
                                planYearText={planYearDetails?.text}
                                fallbackText=""
                                enabled
                            />
                        </div>
                    )}

                    {planYearDetails?.planYearAssets?.length > 0 && (
                        <section className="mt-5">
                            <h2 className={`mb-3 ${styles.planYearAssetTitle}`}>
                                {getText('msg_03e7516d5c17')}
                            </h2>
                            <AssetsRow
                                assets={planYearDetails.planYearAssets}
                                assetDetailsMap={planYearAssetMap}
                            />
                        </section>
                    )}

                    <section className="mt-5">
                        <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-4">
                            <div>
                                <h2 className={`mb-2 ${styles.sectionTitle}`}>
                                    {getText('msg_e2d135ad2078')}
                                </h2>
                                <p
                                    className={`ing-text-heading5 mb-0 ${styles.sectionSubtitle}`}
                                >
                                    {getText('msg_c56d74e0b37a')}
                                </p>
                            </div>
                            <ViewModeToggle
                                selectedViewMode={viewMode}
                                onChange={handleViewModeChange}
                            />
                        </div>
                        {viewMode === BenefitsViewModeEnum.List ? (
                            <BenefitList
                                enrollments={benefitCards}
                                planYearId={selectedPlanYearId}
                                displayEmployeeCosts={displayEmployeeCosts}
                                displayEmployerCosts={displayEmployerCosts}
                            />
                        ) : (
                            <BenefitGrid
                                enrollments={benefitCards}
                                planYearId={selectedPlanYearId}
                            />
                        )}
                    </section>
                </div>
            </div>

            <div className={styles.viewBenefitsBannerWrapper}>
                <div className={styles.viewBenefitsBanner}>
                    <i
                        className={`bi bi-info-circle ${styles.viewBenefitsBannerIcon}`}
                    />
                    <span className={styles.viewBenefitsBannerText}>
                        <strong>You are viewing all Benefits.</strong>{' '}
                        Individuals will only see their elections or plans with
                        a population group they belong to.
                    </span>
                </div>
            </div>
        </div>
    );
}
